import { useState } from 'react';
import { useClientes, useDeletarCliente, useBulkDeletarClientes } from '@/hooks/useClientes';
import { Card, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Eye, Trash2, CheckSquare, Square, Trash } from 'lucide-react';

export function Clientes() {
  const { data: clientes, isLoading } = useClientes();
  const deletarCliente = useDeletarCliente();
  const bulkDeletarCliente = useBulkDeletarClientes();
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const handleDelete = async (id: string) => {
    if (window.confirm('Tem certeza que deseja excluir este cliente?')) {
      await deletarCliente.mutateAsync(id);
    }
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === (clientes?.length || 0)) {
      setSelectedIds([]);
    } else {
      setSelectedIds(clientes?.map((p: any) => p.id) || []);
    }
  };

  const toggleSelect = (id: string) => {
    setSelectedIds(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const handleBulkDelete = () => {
    if (window.confirm(`Deseja excluir os ${selectedIds.length} clientes selecionados?`)) {
      bulkDeletarCliente.mutate(selectedIds, {
        onSuccess: (response: any) => {
          setSelectedIds([]);
          alert(response?.data?.message || 'Exclusão em massa concluída!');
        },
        onError: () => {
          alert('Erro ao realizar exclusão em massa');
        }
      });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Clientes</h1>
          <p className="text-gray-500">Listagem de clientes e usuários do sistema.</p>
        </div>
        <div className="flex gap-3">
          {selectedIds.length > 0 && (
            <Button 
              variant="outline" 
              className="text-red-600 border-red-200 hover:bg-red-50 transition-colors"
              onClick={handleBulkDelete}
              disabled={bulkDeletarCliente.isPending}
            >
              <Trash className="h-4 w-4 mr-2" />
              Excluir ({selectedIds.length})
            </Button>
          )}
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-8 text-center text-gray-500">Carregando...</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="py-4 px-4 w-10">
                      <button onClick={toggleSelectAll} className="text-gray-400 hover:text-orange-600 transition-colors focus:outline-none">
                        {selectedIds.length === (clientes?.length || 0) && selectedIds.length > 0 ? (
                          <CheckSquare className="h-5 w-5 text-[#ED7347]" />
                        ) : (
                          <Square className="h-5 w-5" />
                        )}
                      </button>
                    </th>
                    <th className="py-4 px-6 text-left text-sm font-medium text-gray-500">Nome</th>
                    <th className="py-4 px-6 text-left text-sm font-medium text-gray-500">E-mail</th>
                    <th className="py-4 px-6 text-left text-sm font-medium text-gray-500">Telefone</th>
                    <th className="py-4 px-6 text-left text-sm font-medium text-gray-500">CPF/CNPJ</th>
                    <th className="py-4 px-6 text-right text-sm font-medium text-gray-500">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {clientes?.map((cliente: any) => (
                    <tr key={cliente.id} className={`border-b border-gray-200/50 hover:bg-gray-50 transition-colors ${selectedIds.includes(cliente.id) ? 'bg-orange-50/30' : ''}`}>
                      <td className="py-4 px-4">
                        <button onClick={() => toggleSelect(cliente.id)} className="text-gray-400 hover:text-orange-600 transition-colors focus:outline-none">
                          {selectedIds.includes(cliente.id) ? (
                            <CheckSquare className="h-5 w-5 text-[#ED7347]" />
                          ) : (
                            <Square className="h-5 w-5" />
                          )}
                        </button>
                      </td>
                      <td className="py-4 px-6 font-medium text-gray-900">{cliente.nome}</td>
                      <td className="py-4 px-6 text-gray-500">{cliente.email}</td>
                      <td className="py-4 px-6 text-gray-500">{cliente.telefone || '-'}</td>
                      <td className="py-4 px-6 text-gray-500">{cliente.cpfCnpj || '-'}</td>
                      <td className="py-4 px-6 text-right">
                        <div className="flex justify-end gap-2">
                          <Button variant="ghost" size="sm" onClick={() => alert('Visualizar detalhes do cliente em breve')}>
                            <Eye className="h-4 w-4 text-gray-400" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDelete(cliente.id)}>
                            <Trash2 className="h-4 w-4 text-red-600" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {clientes?.length === 0 && (
                    <tr>
                      <td colSpan={5} className="py-8 text-center text-gray-500">
                        Nenhum cliente encontrado.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
